import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DatabaseIcon,
} from '@heroicons/react/outline';
import { format } from 'date-fns';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

enum SubscriptionStatus {
  Active = 'Active',
  Paused = 'Paused',
  Cancelled = 'Cancelled',
  Finished = 'Finished',
}

function SubscriptionBadge({ status }) {
  let badge = <></>;
  if (status === SubscriptionStatus.Active) {
    badge = (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    );
  }

  if (status === SubscriptionStatus.Paused) {
    badge = (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Paused
      </span>
    );
  }

  if (status === SubscriptionStatus.Cancelled) {
    badge = (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Cancelled
      </span>
    );
  }

  if (status === SubscriptionStatus.Finished) {
    badge = (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Expired
      </span>
    );
  }

  return badge;
}

export function SubscriptionCard({ subscription }) {
  const { register, handleSubmit, setValue, watch, getValues } = useForm();
  const [showLogs, setShowLogs] = useState(false);

  function calculatePaidAmount(payments) {
    return payments?.reduce(
      (partialSum, payment) => partialSum + parseInt(payment.amount, 0),
      0
    );
  }

  const start = new Date(subscription.startsAt);
  const end = new Date(subscription.endsAt);

  return (
    <>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 flex">
          <div className="flex flex-1 space-x-2">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {JSON.parse(subscription.planData).name}
            </h3>
            <div>
              <SubscriptionBadge status={subscription.status} />
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-6 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">Starts At</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5">
                <div className="flex rounded-md shadow-sm">
                  {format(start, 'dd/LL/yyyy')}
                </div>
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-6 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">Ends At</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5">
                <div className="flex rounded-md shadow-sm">
                  {format(end, 'dd/LL/yyyy')}
                </div>
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-6 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">Paid Amount</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5">
                {calculatePaidAmount(subscription.payments)}€
              </dd>
            </div>
            <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-6 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">
                Pending Amount
              </dt>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-5">
                <div className="flex flex-1">
                  {JSON.parse(subscription.planData).price -
                    calculatePaidAmount(subscription.payments)}
                  €
                </div>
              </dd>
            </div>

            <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-6 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">
                Payment history
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5">
                <ul
                  role="list"
                  className="border border-gray-200 rounded-md divide-y divide-gray-200"
                >
                  {subscription.payments?.map((payment) => (
                    <li
                      key={payment.id}
                      className="pl-3 pr-4 py-3 flex items-center justify-between text-sm"
                    >
                      <div className="w-0 flex-1 flex items-center">
                        <CheckCircleIcon
                          className="flex-shrink-0 h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                        <div className="mx-2 flex-shrink-0">
                          <p className="font-medium">{payment.amount}€</p>
                        </div>
                        <span className="flex-1 w-0 truncate">
                          {format(
                            new Date(payment.createdAt),
                            'dd/LL/yy HH:mm'
                          )}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
            <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-6 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">
                {' '}
                <a
                  className="flex flex-1 text-indigo-600 hover:text-indigo-900 cursor-pointer"
                  onClick={() => setShowLogs(!showLogs)}
                >
                  <span className="flex-1">Logs</span>
                  {showLogs ? (
                    <ChevronUpIcon
                      className="flex-shrink-0 h-5 w-5 "
                      aria-hidden="true"
                    />
                  ) : (
                    <ChevronDownIcon
                      className="flex-shrink-0 h-5 w-5 "
                      aria-hidden="true"
                    />
                  )}
                </a>
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5">
                <ul
                  role="list"
                  className="border border-gray-200 rounded-md divide-y divide-gray-200"
                >
                  {showLogs ? (
                    subscription.subscriptionLogs?.map((log) => (
                      <li
                        key={log.id}
                        className="pl-3 pr-4 py-3 flex items-center justify-between text-sm"
                      >
                        <div className="w-0 flex-1 flex items-center">
                          <DatabaseIcon
                            className="flex-shrink-0 h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                          <div className="mx-2 flex-shrink-0">
                            <p className="font-medium">
                              {JSON.stringify(log.transitions.message)}
                            </p>
                          </div>
                          <span className="flex-1 w-0 truncate">
                            {format(new Date(log.createdAt), 'dd/LL/yy HH:mm')}
                          </span>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li
                      key={subscription.subscriptionLogs[0].id}
                      className="pl-3 pr-4 py-3 flex items-center justify-between text-sm"
                    >
                      <div className="w-0 flex-1 flex items-center">
                        <DatabaseIcon
                          className="flex-shrink-0 h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                        <div className="mx-2 flex-shrink-0">
                          <p className="font-medium">
                            {JSON.stringify(
                              subscription.subscriptionLogs[0].transitions
                                .message
                            )}
                          </p>
                        </div>
                        <span className="flex-1 w-0 truncate">
                          {format(
                            new Date(
                              subscription.subscriptionLogs[0].createdAt
                            ),
                            'dd/LL/yy HH:mm'
                          )}
                        </span>
                      </div>
                    </li>
                  )}
                </ul>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </>
  );
}
